import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { api, token } = useAuth();
  const [ads, setAds] = useState([]);
  const [clients, setClients] = useState([]);
  const [tags, setTags] = useState([]);
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAds = useCallback(async (filters = {}) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value);
        }
      });
      const res = await api().get(`/ads?${params.toString()}`);
      setAds(res.data);
    } catch (err) {
      console.error('Error fetching ads:', err);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const fetchClients = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api().get('/clients');
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, [api, token]);

  const fetchTags = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api().get('/tags');
      setTags(res.data);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, [api, token]);

  const fetchCollections = useCallback(async (clientId = null) => {
    if (!token) return;
    try {
      const url = clientId ? `/collections?client_id=${clientId}` : '/collections';
      const res = await api().get(url);
      setCollections(res.data);
    } catch (err) {
      console.error('Error fetching collections:', err);
    }
  }, [api, token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api().get('/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [api, token]);

  const createAd = async (data) => {
    const res = await api().post('/ads', data);
    setAds(prev => [res.data, ...prev]);
    return res.data;
  };

  const uploadAd = async (file, title = null, sourceUrl = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (sourceUrl) formData.append('source_url', sourceUrl);
    
    const res = await api().post('/ads/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    setAds(prev => [res.data, ...prev]);
    return res.data;
  };

  const pasteAd = async (imageData, title = null, sourceUrl = null) => {
    const formData = new FormData();
    formData.append('image_data', imageData);
    if (title) formData.append('title', title);
    if (sourceUrl) formData.append('source_url', sourceUrl);
    
    const res = await api().post('/ads/paste', formData);
    setAds(prev => [res.data, ...prev]);
    return res.data;
  };

  const updateAd = async (adId, data) => {
    const res = await api().patch(`/ads/${adId}`, data);
    setAds(prev => prev.map(ad => ad.id === adId ? res.data : ad));
    return res.data;
  };

  const deleteAd = async (adId) => {
    await api().delete(`/ads/${adId}`);
    setAds(prev => prev.filter(ad => ad.id !== adId));
  };

  const acceptAiTags = async (adId, tagNames) => {
    const res = await api().post(`/ads/${adId}/accept-tags`, tagNames);
    return res.data;
  };

  const createClient = async (data) => {
    const res = await api().post('/clients', data);
    setClients(prev => [...prev, res.data]);
    return res.data;
  };

  const deleteClient = async (clientId) => {
    await api().delete(`/clients/${clientId}`);
    setClients(prev => prev.filter(c => c.id !== clientId));
  };

  const createTag = async (data) => {
    const res = await api().post('/tags', data);
    setTags(prev => [...prev, res.data]);
    return res.data;
  };

  const deleteTag = async (tagId) => {
    await api().delete(`/tags/${tagId}`);
    setTags(prev => prev.filter(t => t.id !== tagId));
  };

  const createCollection = async (data) => {
    const res = await api().post('/collections', data);
    setCollections(prev => [...prev, res.data]);
    return res.data;
  };

  const deleteCollection = async (collectionId) => {
    await api().delete(`/collections/${collectionId}`);
    setCollections(prev => prev.filter(c => c.id !== collectionId));
  };

  const searchAds = async (query, filters = {}) => {
    const res = await api().post('/search', { query, ...filters });
    return res.data;
  };

  const createShareLink = async (data) => {
    const res = await api().post('/shares', data);
    return res.data;
  };

  const getShareLinks = async () => {
    const res = await api().get('/shares');
    return res.data;
  };

  const deleteShareLink = async (shareId) => {
    await api().delete(`/shares/${shareId}`);
  };

  useEffect(() => {
    if (token) {
      fetchClients();
      fetchTags();
      fetchCollections();
      fetchStats();
    }
  }, [token, fetchClients, fetchTags, fetchCollections, fetchStats]);

  return (
    <DataContext.Provider value={{
      ads, clients, tags, collections, stats, loading,
      fetchAds, fetchClients, fetchTags, fetchCollections, fetchStats,
      createAd, uploadAd, pasteAd, updateAd, deleteAd, acceptAiTags,
      createClient, deleteClient,
      createTag, deleteTag,
      createCollection, deleteCollection,
      searchAds, createShareLink, getShareLinks, deleteShareLink
    }}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
